import type {AnnotationModeState} from './AnnotationModeStore';
import type {Annotation, AnnotationType, HighlightColor} from './annotationTypes';

function findAnnotation(
  state: AnnotationModeState,
  regionId: string,
  id: string | undefined
): Annotation | undefined {
  if (!id) {
    return undefined;
  }
  return state.annotationsByRegionId[regionId]?.find((annotation) => annotation.id === id);
}

/** The color of the annotation with `id` in `regionId`, or undefined if absent. */
export function selectHighlightColor(
  state: AnnotationModeState,
  regionId: string,
  id: string | undefined
): HighlightColor | undefined {
  return findAnnotation(state, regionId, id)?.color;
}

/** The style type of the annotation with `id` in `regionId`, or undefined if absent. */
export function selectAnnotationType(
  state: AnnotationModeState,
  regionId: string,
  id: string | undefined
): AnnotationType | undefined {
  return findAnnotation(state, regionId, id)?.type;
}

/** The note of the annotation with `id` in `regionId`, or undefined if absent. */
export function selectAnnotationNote(
  state: AnnotationModeState,
  regionId: string,
  id: string | undefined
): string | undefined {
  return findAnnotation(state, regionId, id)?.note;
}

/**
 * The marks carrying a note. Allocates, so it belongs in a `useMemo` over the
 * region's bucket — a store selector returning this would never stabilize.
 */
export function filterNotedAnnotations(annotations: Annotation[]): Annotation[] {
  return annotations.filter((annotation) => annotation.note !== undefined);
}

/** True while any mounted region has annotatable content. */
export function selectHasAnnotatableContent(state: AnnotationModeState): boolean {
  return state.annotatableContentMounts > 0;
}
